import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const ZanzibarIsland = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">7 DAYS ZANZIBAR ISLAND HOLIDAY</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          7 Days Zanzibar Island Holiday
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Embark on a captivating <strong>7 Days Zanzibar Island Holiday</strong>, where pristine beaches, vibrant marine life, and rich cultural heritage create the perfect tropical escape. From adventurous sea excursions and snorkeling with dolphins to exploring the historic Stone Town and relaxing on white sand beaches, this holiday offers a blend of relaxation and discovery in the heart of Zanzibar.
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 7 Days Zanzibar Island Holiday
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival in Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Upon arrival at Zanzibar Airport, you will be warmly greeted and transferred to a luxurious beach resort on the coast. Check in, relax, and enjoy world-class amenities such as morning pool access, water sports, or scuba diving opportunities, setting the stage for an unforgettable holiday.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Zanzibar Grand Palace or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Safari Blue Sea Adventure</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a delicious breakfast, embark on the Safari Blue Sea Adventure, a full-day excursion by traditional ngalawa (sailing boat). Sail to Kwale Island, where you’ll enjoy swimming, snorkeling, and a grilled seafood lunch featuring fresh fish and lobster. Relax on the beach, explore the island, and return to your beach resort for a restful evening.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Zanzibar Grand Palace or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Swim & Snorkeling with Dolphins</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Start your day with a hearty breakfast, followed by a snorkeling and swimming adventure at Menai Bay, a renowned marine conservation area. Cruise to Kizimkazi, a prime spot for spotting dolphins, and enjoy snorkeling among vibrant coral reefs and marine life. Return to your beach resort for a relaxing evening.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Zanzibar Grand Palace or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 4: Stone Town & Spice Tour</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, explore the historic Stone Town of Zanzibar on a guided tour, visiting landmarks like the House of Wonders, Old Fort, Freddie Mercury House, and the former slave market. Continue with a Spice Tour, discovering Zanzibar’s nickname as the "Spice Island" through plantations of cloves, cinnamon, black pepper, and nutmeg. Enjoy an evening sunset tour in Stone Town before returning to your beach resort.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Zanzibar Grand Palace or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 5: Prison Island Snorkeling & Giant Tortoises</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After breakfast, take a boat to Prison Island (Changuu Island) for a historical tour, including the story of its former prison and the resident giant tortoises. Enjoy snorkeling among vibrant coral reefs and marine life, followed by a relaxing afternoon on the island before returning to your beach resort.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Zanzibar Grand Palace or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 6: Free Day at Beach Resort</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Enjoy a leisurely day at your white sand beach resort after a delicious breakfast. Relax by the pool or engage in optional activities such as kayaking, fishing, or shopping at the bazaar and souvenir shops. Spend the evening soaking in the serene beach atmosphere.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Zanzibar Grand Palace or similar
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 7: Departure from Zanzibar</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a final breakfast, transfer to Stone Town for a last shopping experience at the bazaar and souvenir shops on Mizingani Street, exploring Zanzibar’s charming narrow streets. Afterward, transfer to Zanzibar Airport for your scheduled flight home.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Airport transfers in Zanzibar
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals as stated
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Safari Blue Sea Adventure, Spice Tour, Stone Town tour, and Prison Island tour
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Snorkeling and dolphin swimming excursions
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International flights and visa fees
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities (e.g., kayaking, fishing)
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Alcoholic beverages
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">7 Days Zanzibar Island Holiday</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 7 Days, 6 Nights
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Zanzibar
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Airport pickup - Return to airport
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZanzibarIsland;